/**
 * /historico-trocas — histórico de trocas de plantão (GUPM-104, Fase 2.x).
 *
 * Acesso: engenheiro + admin (operador é redirecionado pra /dashboard
 * via RoleGuard em App.tsx; RLS protege o DB).
 *
 * Filtros: período (data de início/fim). O filtro de barragem saiu no GUPM-273 —
 * o plantão é GLOBAL (cobre A, B e C) e as linhas novas têm `dam_id` nulo, então
 * filtrar por barragem esconderia exatamente os plantões atuais.
 * Lista até 200 registros mais recentes — paginação cabe em GUPM-107 (relatório ANM).
 */

import { useMemo, useState } from 'react';
import { useDataSources } from '@/services/datasources';
import { useHandoverHistory } from './hooks';
import { useQuery } from '@tanstack/react-query';
import {
  countDivergencesFromResponses,
  itemHasDivergence,
} from '@/domain/shift-handover/checklist-rules';
import { diaLocalMenosDias, janelaLocalDeDias } from '@/domain/periodo';
import type { ChecklistItem, ChecklistResponse } from '@/domain/shift-handover/types';
import type { ShiftHandover } from '@/services/datasources/shift-handover';

/**
 * A atestação DESTE turno (GUPM-273).
 *
 * "1 linha = 1 turno": o que a linha atesta é o estado da barragem quando ela
 * ACABOU, não quando começou. `endChecklistItems` é a fonte — inclusive nas
 * linhas históricas, que o backfill preencheu com o checklist da passagem que
 * de fato as encerrou.
 *
 * `checklistItems` é o fallback legado, para o que o backfill não alcançou
 * (linha sem sucessora). Turno em curso ainda não atestou nada: lista vazia.
 */
function atestacaoDoTurno(h: ShiftHandover): ChecklistResponse[] {
  return h.endChecklistItems ?? h.checklistItems ?? [];
}


export default function HistoricoTrocasPage() {
  const { shiftHandover } = useDataSources();

  const [from, setFrom] = useState<string>(diaLocalMenosDias(30));
  const [to, setTo] = useState<string>(diaLocalMenosDias(0));

  // Sem filtro de barragem (GUPM-273): o plantão é GLOBAL, cobre A, B e C. Pior
  // que inútil, o filtro seria uma armadilha — toda linha criada a partir deste
  // card tem `dam_id` nulo, então escolher uma barragem esconderia justamente os
  // plantões atuais e mostraria só o resíduo histórico.
  // Dia do filtro = dia do fuso de quem lê, não o dia-calendário UTC. O limite
  // em `Z` terminava a janela às 20:59 locais e derrubava do histórico o turno
  // da noite — justamente o que importa num incidente noturno.
  const filter = useMemo(() => janelaLocalDeDias(from, to), [from, to]);

  const { data: handovers = [], isLoading, error } = useHandoverHistory(filter);

  // catálogo serve pra montar a tabela expandida (label do item)
  const { data: catalog = [] } = useQuery({
    queryKey: ['shift-checklist-catalog-history'],
    staleTime: 5 * 60 * 1000,
    queryFn: () => shiftHandover.listChecklistItems(),
  });

  const catalogById = useMemo(() => {
    const map = new Map<string, ChecklistItem>();
    for (const c of catalog) map.set(c.id, c);
    return map;
  }, [catalog]);

  const [expandedId, setExpandedId] = useState<string | null>(null);

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs uppercase tracking-wide text-jf-dim">Auditoria</p>
          <h1 className="text-2xl font-bold text-white">Histórico de Trocas de Plantão</h1>
          <p className="text-sm text-jf-muted">
            Registros auditáveis das passagens de turno. Compliance ANM Res. 95/2022.
          </p>
        </div>
      </header>

      {/* Filtros */}
      <section className="grid gap-3 sm:grid-cols-3 rounded-lg border border-jf-border bg-jf-surface2 p-4">
        <div>
          <label className="block text-[10px] uppercase tracking-wide text-jf-dim mb-1">De</label>
          <input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="h-[36px] w-full rounded-md border border-jf-border bg-jf-surface px-3 text-sm text-white outline-none focus:border-jf-yellow"
          />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-wide text-jf-dim mb-1">Até</label>
          <input
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="h-[36px] w-full rounded-md border border-jf-border bg-jf-surface px-3 text-sm text-white outline-none focus:border-jf-yellow"
          />
        </div>
        <div className="flex items-end">
          <span className="text-xs text-jf-muted">
            {isLoading ? 'Carregando…' : `${handovers.length} registro${handovers.length !== 1 ? 's' : ''}`}
          </span>
        </div>
      </section>

      {error && (
        <div role="alert" className="rounded-md border border-red-500 bg-red-500/10 px-3 py-2 text-sm text-red-300">
          Falha ao carregar histórico: {error instanceof Error ? error.message : 'erro desconhecido'}
        </div>
      )}

      {/* Tabela */}
      {/* `overflow-hidden` (para arredondar o canto) CORTAVA a tabela em telas
          estreitas: as colunas da direita simplesmente sumiam, sem barra e sem
          aviso — um auditor no celular lia a linha achando que viu tudo. Com
          `overflow-x-auto` + `min-w` a tabela rola em vez de se espremer. */}
      <section className="rounded-lg border border-jf-border bg-jf-surface overflow-x-auto">
        <table className="w-full min-w-[880px] text-sm">
          <thead className="bg-jf-surface2 text-[10px] uppercase tracking-wide text-jf-dim">
            <tr>
              {/* Cabeçalhos do modelo em que 1 linha = 1 TROCA ("Saindo",
                  "Entrante", "Status") descreviam todos o MESMO evento, e por
                  isso podiam ser lidos em sequência. Com 1 linha = 1 TURNO
                  (GUPM-273) cada um passou a falar de um momento diferente:
                  as pessoas contam como o turno COMEÇOU, o status conta como
                  TERMINOU. Lida da esquerda pra direita, a linha de um turno
                  recebido por rendição e encerrado solo afirmava "Vigilante 1
                  saiu, Vigilante 2 entrou — SEM RENDIÇÃO"; aquela passagem teve
                  rendição, sem rendição ficou o fim do turno, uma hora depois.
                  Cada célula agora anuncia de que momento fala. */}
              <th className="px-3 py-2 text-left">Período</th>
              <th className="px-3 py-2 text-left">Plantonista</th>
              <th className="px-3 py-2 text-left">Como começou</th>
              {/* Sem coluna de barragem: o plantão cobre as três (GUPM-273). A
                  barragem gravada nas linhas antigas sobrevive no detalhe
                  expandido — é resíduo do modelo anterior, e apagá-la de vez
                  seria pior que mostrá-la onde não confunde. */}
              <th className="px-3 py-2 text-center">Como terminou</th>
              <th className="px-3 py-2 text-left">Observação</th>
              <th className="px-3 py-2 text-right"></th>
            </tr>
          </thead>
          <tbody>
            {!isLoading && handovers.length === 0 && (
              <tr>
                <td colSpan={6} className="px-3 py-8 text-center text-jf-muted text-sm">
                  Nenhum registro no período.
                </td>
              </tr>
            )}
            {handovers.map((h) => {
              // Divergências (zombie-aware) via a função consolidada do domínio —
              // mesma fonte de verdade usada pelo drill-down da /auditoria (GUPM-147).
              const atestacao = atestacaoDoTurno(h);
              const divergencias = countDivergencesFromResponses(atestacao, catalogById);
              const isExpanded = expandedId === h.id;
              return (
                <RowAndDetail
                  key={h.id}
                  handover={h}
                  divergencias={divergencias}
                  temAtestacao={atestacao.length > 0}
                  expanded={isExpanded}
                  catalogById={catalogById}
                  onToggle={() => setExpandedId(isExpanded ? null : h.id)}
                />
              );
            })}
          </tbody>
        </table>
      </section>

      <p className="text-xs text-jf-dim">
        Limite atual: 200 registros mais recentes. Para export PDF/CSV (ANM), ver{' '}
        <a
          href="https://jfbrasil.youtrack.cloud/issue/GUPM-107"
          target="_blank"
          rel="noreferrer"
          className="text-jf-yellow hover:underline"
        >
          GUPM-107
        </a>
        .
      </p>
    </div>
  );
}

const BADGE_BASE = 'rounded-full px-2 py-0.5 text-[10px] font-bold uppercase';

/**
 * Como o turno terminou, em palavras. "Plantão encerrado" para tudo descrevia
 * bem quando encerrar era exceção (GUPM-272); com todo turno encerrado, a
 * palavra deixou de distinguir rotina de anomalia.
 */
const ENCERRAMENTO_LABEL: Record<'rendicao' | 'solo' | 'admin', string> = {
  rendicao: 'Turno encerrado com passagem',
  solo: 'Turno encerrado SEM rendição',
  admin: 'Destrave administrativo',
};

/**
 * Período do turno — início → fim.
 *
 * A coluna mostrava só `completedAt` porque no modelo antigo a linha ERA o
 * instante da troca; não havia duração a exibir. Com 1 linha = 1 turno a
 * duração passa a existir, e é justamente o que a ANM Res. 95/2022 cobra do
 * registro de vigilância — antes só saía expandindo linha por linha.
 *
 * Turno aberto fecha com "em curso" e não com uma hora inventada.
 */
function periodoDoTurno(handover: import('@/services/datasources').ShiftHandover): string {
  const inicio = new Date(handover.completedAt);
  const abertura = inicio.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });

  if (!handover.endedAt) return `${abertura} → em curso`;

  const fim = new Date(handover.endedAt);
  // Turno que vira o dia repete a data; o de sempre mostra só a hora, senão a
  // coluna fica com duas datas iguais lado a lado e o olho perde a duração.
  const fechamento =
    fim.toDateString() === inicio.toDateString()
      ? fim.toLocaleTimeString('pt-BR', { timeStyle: 'short' })
      : fim.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });

  return `${abertura} → ${fechamento}`;
}

/**
 * De que momento é o texto da coluna "Observação", e qual texto é.
 *
 * A coluna mostra `observation` (escrito na ABERTURA) ou, quando não há,
 * `end_reason` (escrito no ENCERRAMENTO) — porque a RPC grava a linha nova da
 * rendição com `observation` nulo e o texto do operador vira `end_reason` da
 * linha que fecha. Sem o rótulo, a mesma coluna troca de significado de uma
 * linha para a outra, no mesmo formato, sem nada avisando o auditor.
 */
function observacaoDoTurno(
  handover: import('@/services/datasources').ShiftHandover,
): { momento: 'abertura' | 'encerramento'; texto: string } | null {
  if (handover.observation) return { momento: 'abertura', texto: handover.observation };
  if (handover.endReason) return { momento: 'encerramento', texto: handover.endReason };
  return null;
}

/**
 * Status do turno, decidido por `endKind` — GUPM-273.
 *
 * A versão anterior decidia pela PRESENÇA de `endedAt`, o que fazia sentido no
 * GUPM-272: lá `ended_at` só existia em turno interrompido. Com "1 linha = 1
 * turno" todo turno concluído tem `ended_at`, e a mesma regra passaria a pintar
 * toda passagem de rotina de vermelho "Encerrado".
 *
 * A pergunta que este badge responde é "como este turno terminou, e o que quem
 * saiu atestou" — não "ele terminou?".
 */
function StatusDoTurno({
  handover,
  divergencias,
  temAtestacao,
}: {
  handover: import('@/services/datasources').ShiftHandover;
  divergencias: number;
  /** Existe checklist preenchido nesta linha (de saída, ou o legado de abertura). */
  temAtestacao: boolean;
}) {
  // Destrave: o admin não viveu o turno, `end_checklist_items` é nulo por
  // desenho. Não há atestação para resumir — a intervenção é a informação.
  if (handover.endKind === 'admin') {
    return (
      <span title={handover.endReason ?? undefined} className={`${BADGE_BASE} bg-red-500/15 text-red-300`}>
        Destravado
      </span>
    );
  }

  // Saída sem substituto: a operação ficou desguarnecida. Vale destaque mesmo
  // com o checklist todo conforme.
  if (handover.endKind === 'solo') {
    return (
      <span title={handover.endReason ?? undefined} className={`${BADGE_BASE} bg-amber-500/15 text-amber-300`}>
        Sem rendição
      </span>
    );
  }

  // Sem nada atestado, o badge fala do ANDAMENTO e não do estado da barragem.
  //
  // O gatilho é a ausência de atestação, e não `endedAt`, porque as duas coisas
  // se descolaram: no modelo novo o turno em curso não tem checklist (a
  // atestação é do fim), mas uma linha LEGADA aberta tem — o checklist de
  // abertura do modelo antigo. Resumir essa atestação é mais informativo que
  // dizer "em curso"; e dizer "OK" sobre um turno sem atestação nenhuma seria a
  // mentira do GUPM-247 com outra fantasia.
  if (!temAtestacao) {
    return (
      <span className={`${BADGE_BASE} bg-blue-500/15 text-blue-300`}>
        {handover.endedAt ? 'Sem atestação' : 'Em curso'}
      </span>
    );
  }

  // Rendição (e linha histórica sem `end_kind`, anterior ao backfill): o que
  // interessa é o que o checklist de saída apontou.
  return divergencias === 0 ? (
    <span className={`${BADGE_BASE} bg-green-500/15 text-green-300`}>OK</span>
  ) : (
    <span
      title={handover.endReason ?? undefined}
      className={`${BADGE_BASE} bg-orange-500/15 text-orange-300`}
    >
      {divergencias} divergência{divergencias > 1 ? 's' : ''}
    </span>
  );
}

interface RowProps {
  handover: import('@/services/datasources').ShiftHandover;
  divergencias: number;
  temAtestacao: boolean;
  expanded: boolean;
  catalogById: Map<string, ChecklistItem>;
  onToggle: () => void;
}

function RowAndDetail({
  handover,
  divergencias,
  temAtestacao,
  expanded,
  catalogById,
  onToggle,
}: RowProps) {
  const observacao = observacaoDoTurno(handover);

  return (
    <>
      <tr className="border-t border-jf-border hover:bg-jf-surface2 cursor-pointer" onClick={onToggle}>
        <td className="px-3 py-2 text-white tabular-nums whitespace-nowrap">
          {periodoDoTurno(handover)}
        </td>
        {/* De quem é o turno. Era "Entrante" — palavra do evento de troca, que
            numa linha que é um turno faz a pessoa parecer parte do fim. */}
        <td className="px-3 py-2 text-white">{handover.operatorInName}</td>
        <td className="px-3 py-2">
          {handover.operatorOutId === null ? (
            // Antes isto morava na coluna "Saindo": uma coluna de PESSOA
            // exibindo, em itálico, a explicação de por que não há pessoa. O
            // contorno existia por falta de uma coluna que dissesse como o
            // turno começou — que é o que a abertura solo é (GUPM-272/273).
            <span className="text-jf-muted italic">Abertura sem passagem</span>
          ) : (
            <span className="text-white">{`Recebeu de ${handover.operatorOutName ?? '—'}`}</span>
          )}
        </td>
        <td className="px-3 py-2 text-center">
          {/* O badge é decidido por `endKind`, não pela PRESENÇA de `endedAt`.
              No GUPM-272 `ended_at` significava "turno interrompido" e era
              exceção; com "1 linha = 1 turno" (GUPM-273) TODO turno concluído
              passa a ter `ended_at`, e testar a presença pintaria toda rendição
              de rotina de vermelho "Encerrado" — a contagem de divergências
              viraria código morto e o único badge alcançável seria o do turno em
              curso, que por construção ainda não atestou nada e sairia verde
              "OK". Dizer "sem divergência" sobre um turno que não foi atestado é
              o mesmo defeito do GUPM-247, com outra fantasia. */}
          <StatusDoTurno
            handover={handover}
            divergencias={divergencias}
            temAtestacao={temAtestacao}
          />
        </td>
        <td className="px-3 py-2 text-jf-muted text-xs truncate max-w-[280px]">
          {/* `endReason` como alternativa NÃO é enfeite: desde o GUPM-273 a RPC
              grava a linha nova da rendição com `observation` NULO — o texto que
              o operador escreveu na troca vira `end_reason` da linha que fecha.
              Lendo só `observation`, esta coluna ficaria "—" em toda troca, e a
              justificativa de divergência sumiria da varredura do auditor,
              sobrando só ao expandir linha por linha.

              O rótulo do momento é o que impede a coluna de trocar de
              significado em silêncio de uma linha para a outra. */}
          {observacao ? (
            <>
              <span className="uppercase tracking-wide text-[10px] text-jf-dim">
                {`${observacao.momento} ·`}
              </span>{' '}
              <span className="italic">{observacao.texto}</span>
            </>
          ) : (
            '—'
          )}
        </td>
        <td className="px-3 py-2 text-right text-jf-muted">{expanded ? '▴' : '▾'}</td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={6} className="bg-jf-surface2/40 px-4 py-4">
            {/* Barragem só nas linhas que a têm gravada — as anteriores ao
                GUPM-273, quando `dam_id` era obrigatório e a UI resolvia uma
                barragem só para conseguir escrever. O plantão sempre cobriu as
                três; o campo era exigência de esquema, não escopo do turno.
                Fica aqui, no detalhe, porque apagar dado auditável gravado é
                pior que exibi-lo onde não induz a erro. */}
            {handover.damCode && (
              <p className="mb-3 text-[11px] text-jf-dim">
                {`Barragem registrada na linha: ${handover.damCode} — registro anterior ao plantão global; a vigilância sempre cobriu A, B e C.`}
              </p>
            )}
            <div className="grid gap-3 sm:grid-cols-2">
              {atestacaoDoTurno(handover)
                .map((resp) => ({ resp, item: catalogById.get(resp.item_id) }))
                .sort((a, b) => (a.item?.order_index ?? 9999) - (b.item?.order_index ?? 9999))
                .map(({ resp, item }) => {
                  // Zombie (item fora do catálogo) sempre diverge — auditor vê que
                  // perdeu o contexto da pergunta. Mesma regra do domínio.
                  const diverge = !item || itemHasDivergence(item, resp);
                  return (
                    <div
                      key={resp.item_id}
                      className={`rounded-md border p-2 text-xs ${
                        diverge
                          ? 'border-orange-500/50 bg-orange-500/5'
                          : 'border-jf-border bg-jf-surface'
                      }`}
                    >
                      <span className="font-bold text-jf-dim mr-1">{item?.order_index ?? '?'}.</span>
                      <span className="text-white">{item?.label ?? '(item removido do catálogo)'}</span>
                      <span
                        className={`ml-2 inline-block rounded px-1.5 py-0.5 text-[10px] font-bold uppercase ${
                          resp.answer === 'sim'
                            ? 'bg-green-500/20 text-green-300'
                            : 'bg-red-500/20 text-red-300'
                        }`}
                      >
                        {resp.answer ?? '—'}
                      </span>
                    </div>
                  );
                })}
            </div>
            {handover.observation && (
              <div className="mt-3 rounded-md border border-jf-border bg-jf-surface p-3">
                <p className="text-[10px] uppercase tracking-wide text-jf-dim mb-1">Observação</p>
                <p className="text-sm text-white whitespace-pre-wrap">{handover.observation}</p>
              </div>
            )}
            {handover.endedAt && (
              // Quem encerrou, quando e por quê — antes isso só existia no
              // audit_log, longe de quem lê o histórico de plantão.
              // Vermelho só onde é anomalia. Desde o GUPM-273 TODO turno é
              // encerrado, então pintar de vermelho toda passagem de rotina
              // gastaria o alarme justamente onde ele precisa ser lido —
              // destrave e saída sem rendição.
              <div
                className={`mt-3 rounded-md border p-3 ${
                  handover.endKind === 'rendicao'
                    ? 'border-jf-border bg-jf-surface'
                    : 'border-red-500/40 bg-red-500/5'
                }`}
              >
                <p
                  className={`text-[10px] uppercase tracking-wide mb-1 ${
                    handover.endKind === 'rendicao' ? 'text-jf-dim' : 'text-red-300'
                  }`}
                >
                  {`${ENCERRAMENTO_LABEL[handover.endKind ?? 'rendicao']} por ${
                    handover.endedByName ?? '—'
                  } em ${new Date(handover.endedAt).toLocaleString('pt-BR', {
                    dateStyle: 'short',
                    timeStyle: 'short',
                  })}`}
                </p>
                <p className="text-sm text-white whitespace-pre-wrap">{handover.endReason ?? '—'}</p>
                <p className="mt-1 text-[10px] text-jf-muted">
                  {handover.endChecklistItems
                    ? `Checklist de saída preenchido (${handover.endChecklistItems.length} itens).`
                    : 'Sem checklist de saída — destrave administrativo.'}
                </p>
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}
